import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { toast } from "sonner";
import { Building2, TrendingUp, CreditCard, Users, FileText, ChevronRight, ChevronLeft, Plus, Trash2 } from "lucide-react";

const CURRENCIES = ["INR", "USD", "EUR", "GBP", "JPY", "AUD", "CAD", "CHF", "SGD", "AED"];
const DEBT_TYPES = ["Working Capital", "Short Term Loan", "Long Term Loan", "Term Loan", "Overdraft", "Letter of Credit", "Buyers Credit"];
const PAYMENT_FREQUENCIES = ["Monthly", "Quarterly", "Semi-Annual", "Annual", "Bullet"];
const INVESTMENT_GROUPS = ["Bank Deposit", "Corporate Deposit", "Government", "Equity", "Cash"];
const INSTRUMENT_MAP = {
  "Bank Deposit":      ["Term Deposit", "Certificate of Deposit"],
  "Corporate Deposit": ["Commercial Paper", "Non Convertible Debenture"],
  "Government":        ["Government Securities (GSec)", "Treasury Bills (T-Bills)"],
  "Equity":            ["Mutual Fund", "Stock"],
  "Cash":              ["Current Account", "Savings Account", "Overnight Deposit"],
};

// ── Unit handling ──────────────────────────────────────────────────────────
// The UI collects ₹ crore (and USD million). The database stores absolute
// units, so every amount is converted back on submit.
const USD_RATE = 95;   // 1 USD = ₹95
const CR = 1e7;        // 1 crore
const MN = 1e6;        // 1 million

// ₹ Cr → USD Mn : (Cr × 1,00,00,000) ÷ 95 ÷ 10,00,000
const crToUsdMn = (cr) => {
  const n = parseFloat(cr);
  return isNaN(n) ? "" : (n * 10 / USD_RATE).toFixed(2);
};

const crToAbs = (v) => { const n = parseFloat(v); return isNaN(n) ? null : n * CR; };
const mnToAbs = (v) => { const n = parseFloat(v); return isNaN(n) ? null : n * MN; };

// Debt is entered in ₹ Cr for INR and in millions for every other currency
const debtToAbs = (v, currency) => {
  const n = parseFloat(v);
  return isNaN(n) ? null : n * (currency === "INR" ? CR : MN);
};
const debtUnitLabel = (currency) => (currency === "INR" ? "₹ Cr" : `${currency} Mn`);

const steps = [
  { id: 1, title: "Company Basics",        icon: Building2, desc: "Tell us about your company"   },
  { id: 2, title: "Financial Performance",  icon: TrendingUp,desc: "Your financial history"       },
  { id: 3, title: "Debt & Investments",     icon: CreditCard,desc: "Your financial positions"     },
  { id: 4, title: "People & Stakeholders",  icon: Users,     desc: "Your key people"              },
  { id: 5, title: "Documents",              icon: FileText,  desc: "Upload your balance sheets"   },
];

function formatDateInput(value) {
  const digits = value.replace(/\D/g, "");
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4, 8)}`;
}

function toISO(ddmmyyyy) {
  if (!ddmmyyyy || ddmmyyyy.length !== 10) return null;
  const [dd, mm, yyyy] = ddmmyyyy.split("-");
  const day   = parseInt(dd, 10);
  const month = parseInt(mm, 10);
  const year  = parseInt(yyyy, 10);

  // Basic range checks
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31)     return null;
  if (year < 2000 || year > 2050) return null;

  // Check actual days in month
  const daysInMonth = new Date(year, month, 0).getDate();
  if (day > daysInMonth) return null; // e.g. 31-09 fails here

  return `${yyyy}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export default function Onboarding() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading,     setLoading]     = useState(false);

  // ── Gate: only first user of a company can onboard ───────────────────────
  useEffect(() => {
    const checkFirstUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { navigate("/login"); return; }

      const { data: existing } = await supabase
        .from("company_users")
        .select("id")
        .eq("user_id", user.id)
        .maybeSingle();

      if (existing) {
        // Already linked to a company — skip onboarding
        navigate("/dashboard");
      }
    };
    checkFirstUser();
  }, []);

  // ── Step 1 ────────────────────────────────────────────────────────────────
  const [companyName,     setCompanyName]     = useState("");
  const [registeredOn,    setRegisteredOn]    = useState("");
  const [industry,        setIndustry]        = useState("");
  const [country,         setCountry]         = useState("");
  const [state,           setState]           = useState("");
  const [district,        setDistrict]        = useState("");
  const [employees,       setEmployees]       = useState("");
  const [totalImportsINR, setTotalImportsINR] = useState("");   // ₹ Cr
  const [totalImportsUSD, setTotalImportsUSD] = useState("");   // USD Mn (derived)
  const [totalExportsINR, setTotalExportsINR] = useState("");   // ₹ Cr
  const [totalExportsUSD, setTotalExportsUSD] = useState("");   // USD Mn (derived)
  const [companyBrief,    setCompanyBrief]    = useState("");

  // ── Step 2 ────────────────────────────────────────────────────────────────
  const [financials, setFinancials] = useState([
    { year: "", sales: "", ebit: "", pnl: "" },
    { year: "", sales: "", ebit: "", pnl: "" },
    { year: "", sales: "", ebit: "", pnl: "" },
  ]);
  const [keySuppliers, setKeySuppliers] = useState([{ name: "", country: "" }]);
  const [keyBuyers,    setKeyBuyers]    = useState([{ name: "", country: "" }]);

  // ── Step 3 ────────────────────────────────────────────────────────────────
  const [debtProfile, setDebtProfile] = useState([{
    debt_type: "", provider: "", currency: "INR",
    loan_amount: "", outstanding_balance: "", interest_rate: "",
    market_benchmark: "", start_date: "", maturity_date: "",
    payment_frequency: "", rating: "", rating_provider: "",
  }]);
  const [investmentProfile, setInvestmentProfile] = useState([{
    group_type: "", instrument_type: "", instrument_name: "",
    placed_with: "", amount: "", start_date: "", end_date: "",
    rate: "", status: "Active",
  }]);

  // ── Step 4 ────────────────────────────────────────────────────────────────
  const [promoterDetails, setPromoterDetails] = useState([{ name: "", stake: "" }]);
  const [keyPersonnel,    setKeyPersonnel]    = useState([
    { role: "CEO",          name: "" },
    { role: "Finance Head", name: "" },
    { role: "Head Sales",   name: "" },
  ]);
  const [auditorDetails, setAuditorDetails] = useState("");

  // ── Step 5 ────────────────────────────────────────────────────────────────
  const [doc1, setDoc1] = useState(null);
  const [doc2, setDoc2] = useState(null);
  const [doc3, setDoc3] = useState(null);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const updateFinancial = (index, field, value) => {
    const updated = [...financials];
    updated[index][field] = value;
    setFinancials(updated);
  };

  const addRow    = (setter, getter, template) => setter([...getter, { ...template }]);
  const removeRow = (setter, getter, index)    => setter(getter.filter((_, i) => i !== index));
  const updateRow = (setter, getter, index, field, value) => {
    const updated = [...getter];
    updated[index][field] = value;
    setter(updated);
  };

  // ── Validation ────────────────────────────────────────────────────────────
  const validateStep = (step) => {
    if (step === 1) {
      if (!companyName.trim())     { toast.error("Company name is required.");           return false; }
      if (!industry.trim())        { toast.error("Industry / Sector is required.");      return false; }
      if (!country.trim())         { toast.error("Country is required.");                return false; }
      if (!state.trim())           { toast.error("State is required.");                  return false; }
      if (!district.trim())        { toast.error("District is required.");               return false; }
      if (!totalImportsINR.trim() || isNaN(parseFloat(totalImportsINR))) { toast.error("Total Imports (₹ Cr) is required."); return false; }
      if (!totalExportsINR.trim() || isNaN(parseFloat(totalExportsINR))) { toast.error("Total Exports (₹ Cr) is required."); return false; }
      if (!companyBrief.trim())    { toast.error("Company brief is required.");          return false; }
      return true;
    }

    if (step === 3) {
      for (let i = 0; i < debtProfile.length; i++) {
        const d = debtProfile[i];
        const unit = debtUnitLabel(d.currency);
        if (!d.debt_type)                                                            { toast.error(`Debt row ${i+1}: Debt type is required.`);                                          return false; }
        if (!d.provider.trim())                                                      { toast.error(`Debt row ${i+1}: Provider is required.`);                                           return false; }
        if (!d.currency)                                                             { toast.error(`Debt row ${i+1}: Currency is required.`);                                           return false; }
        if (!d.loan_amount || isNaN(parseFloat(d.loan_amount)))                      { toast.error(`Debt row ${i+1}: Loan amount (${unit}) is required.`);                              return false; }
        if (!d.outstanding_balance || isNaN(parseFloat(d.outstanding_balance)))      { toast.error(`Debt row ${i+1}: Outstanding balance (${unit}) is required.`);                      return false; }
        if (parseFloat(d.outstanding_balance) > parseFloat(d.loan_amount))           { toast.error(`Debt row ${i+1}: Outstanding cannot exceed loan amount.`);                          return false; }
        if (!d.interest_rate || isNaN(parseFloat(d.interest_rate)))                  { toast.error(`Debt row ${i+1}: Interest rate is required.`);                                      return false; }
        if (!d.market_benchmark || isNaN(parseFloat(d.market_benchmark)))            { toast.error(`Debt row ${i+1}: Market benchmark is required.`);                                   return false; }
        if (!d.start_date || d.start_date.length !== 10 || !toISO(d.start_date))     { toast.error(`Debt row ${i+1}: Start date is invalid. Use a real date in DD-MM-YYYY.`);          return false; }
        if (!d.maturity_date || d.maturity_date.length !== 10 || !toISO(d.maturity_date)) { toast.error(`Debt row ${i+1}: Maturity date is invalid. Use a real date in DD-MM-YYYY.`); return false; }
        if (!d.payment_frequency)                                                    { toast.error(`Debt row ${i+1}: Payment frequency is required.`);                                  return false; }
        if (!d.rating.trim())                                                        { toast.error(`Debt row ${i+1}: Rating is required.`);                                             return false; }
        if (!d.rating_provider.trim())                                               { toast.error(`Debt row ${i+1}: Rating provider is required.`);                                    return false; }
      }

      for (let i = 0; i < investmentProfile.length; i++) {
        const inv = investmentProfile[i];
        if (!inv.group_type)                                                            { toast.error(`Investment row ${i+1}: Group is required.`);                                     return false; }
        if (!inv.instrument_type)                                                       { toast.error(`Investment row ${i+1}: Instrument type is required.`);                           return false; }
        if (!inv.instrument_name.trim())                                                { toast.error(`Investment row ${i+1}: Name is required.`);                                      return false; }
        if (!inv.placed_with.trim())                                                    { toast.error(`Investment row ${i+1}: Placed with is required.`);                               return false; }
        if (!inv.amount || isNaN(parseFloat(inv.amount)))                               { toast.error(`Investment row ${i+1}: Amount (₹ Cr) is required.`);                             return false; }
        if (!inv.start_date || inv.start_date.length !== 10 || !toISO(inv.start_date))  { toast.error(`Investment row ${i+1}: Start date is invalid. Use a real date in DD-MM-YYYY.`); return false; }
        if (inv.instrument_type !== "Mutual Fund" && inv.instrument_type !== "Stock") {
          if (!inv.end_date || inv.end_date.length !== 10 || !toISO(inv.end_date))      { toast.error(`Investment row ${i+1}: End date is invalid. Use a real date in DD-MM-YYYY.`);   return false; }
        }
        if (!inv.rate || isNaN(parseFloat(inv.rate)))                                   { toast.error(`Investment row ${i+1}: Rate is required.`);                                      return false; }
        if (!inv.status)                                                                { toast.error(`Investment row ${i+1}: Status is required.`);                                    return false; }
      }

      return true;
    }

    return true; // steps 2, 4, 5 — no mandatory validation
  };

  const handleNext = () => {
    const result = validateStep(currentStep);
    if (!result) return;
    setCurrentStep(s => s + 1);
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;
    setLoading(true);
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) { toast.error("You must be logged in."); setLoading(false); return; }

      // 1. Create company + link user via secure function
      const { data: companyId, error: fnError } = await supabase
        .rpc('create_company_on_signup', {
          p_company_name: companyName,
          p_user_id:      user.id,
        });
      if (fnError) { toast.error(`Failed to create company: ${fnError.message}`); setLoading(false); return; }

      // 3. Create company profile — all amounts converted back to absolute units
      const { error: profileError } = await supabase.from("company_profiles").insert({
        user_id:              user.id,
        company_id:           companyId,
        created_by_user_id:   user.id,
        company_name:         companyName,
        registered_on:        registeredOn || null,
        industry,
        country,
        state,
        district,
        employees:            employees || null,
        total_imports_inr:    crToAbs(totalImportsINR),
        total_imports_usd:    mnToAbs(totalImportsUSD),
        total_exports_inr:    crToAbs(totalExportsINR),
        total_exports_usd:    mnToAbs(totalExportsUSD),
        company_brief:        companyBrief,
        financials:           financials.map(f => ({
                                ...f,
                                sales: crToAbs(f.sales),
                                ebit:  crToAbs(f.ebit),
                                pnl:   crToAbs(f.pnl),
                              })),
        key_suppliers:        keySuppliers,
        key_buyers:           keyBuyers,
        debt_profile:         debtProfile.map(d => ({
                                ...d,
                                loan_amount:         debtToAbs(d.loan_amount, d.currency),
                                outstanding_balance: debtToAbs(d.outstanding_balance, d.currency),
                              })),
        investment_profile:   investmentProfile.map(inv => ({
                                ...inv,
                                amount: crToAbs(inv.amount),
                              })),
        promoter_details:     promoterDetails,
        key_personnel:        keyPersonnel,
        auditor_details:      auditorDetails || null,
        onboarding_complete:  true,
      });
      if (profileError) { toast.error(`Failed to save profile: ${profileError.message}`); setLoading(false); return; }

      // 4. Save debt positions
      const validDebt = debtProfile.filter(d => d.provider && d.loan_amount);
      if (validDebt.length > 0) {
        const { error: debtError } = await supabase.from("debt_positions").insert(validDebt.map(d => ({
          company_id:          companyId,
          debt_type:           d.debt_type,
          provider:            d.provider,
          currency:            d.currency,
          loan_amount:         debtToAbs(d.loan_amount, d.currency),
          outstanding_balance: debtToAbs(d.outstanding_balance, d.currency),
          interest_rate:       parseFloat(d.interest_rate),
          market_benchmark:    parseFloat(d.market_benchmark),
          start_date:          toISO(d.start_date),
          maturity_date:       toISO(d.maturity_date),
          payment_frequency:   d.payment_frequency,
          rating:              d.rating,
          rating_provider:     d.rating_provider,
        })));
        if (debtError) {
          console.error("Debt insert error:", debtError);
          toast.error(`Debt data could not be saved: ${debtError.message}`);
        }
      }

      // 5. Save investments
      const validInv = investmentProfile.filter(inv => inv.placed_with && inv.amount);
      if (validInv.length > 0) {
        const { error: invError } = await supabase.from("investments").insert(validInv.map(inv => ({
          company_id:      companyId,
          group_type:      inv.group_type,
          instrument_type: inv.instrument_type,
          instrument_name: inv.instrument_name,
          placed_with:     inv.placed_with,
          amount:          crToAbs(inv.amount),
          start_date:      toISO(inv.start_date),
          end_date:        toISO(inv.end_date),
          rate:            parseFloat(inv.rate),
          status:          inv.status,
        })));
        if (invError) {
          console.error("Investment insert error:", invError);
          toast.error(`Investment data could not be saved: ${invError.message}`);
        }
      }

      toast.success("Profile saved successfully!");
      navigate("/dashboard");
    } catch (error) {
      console.error("Submit error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#0a0b0f] py-12 px-6">
      <div className="max-w-4xl mx-auto">

        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-14 h-14 rounded-2xl gradient-primary flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-bold text-2xl">N</span>
          </div>
          <h1 className="text-3xl font-bold text-white mb-3">Welcome to Nebula Finance AI</h1>
          <p className="text-gray-400 max-w-2xl mx-auto text-sm leading-relaxed">
            To provide you with precise financial guidance, we need to understand your business in depth.
            <span className="text-indigo-400"> This is a one-time setup.</span>
          </p>
        </div>

        {/* Step indicators */}
        <div className="flex items-center justify-center mb-10 gap-2 flex-wrap">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center gap-2">
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                currentStep === step.id ? "bg-indigo-600 text-white" :
                currentStep > step.id  ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30" :
                                          "bg-[#1a1d26] text-gray-400 border border-[#2a2d3a]"
              }`}>
                <step.icon className="w-4 h-4" />
                <span className="hidden sm:inline">{step.title}</span>
                <span className="sm:hidden">{step.id}</span>
              </div>
              {index < steps.length - 1 && <ChevronRight className="w-4 h-4 text-gray-600" />}
            </div>
          ))}
        </div>

        {/* Form Card */}
        <div className="nebula-card p-8">

          {/* ── STEP 1 ── */}
          {currentStep === 1 && (
            <div>
              <h2 className="text-xl font-bold text-white mb-2">Company Basics</h2>
              <p className="text-gray-400 text-sm mb-6">
                Fields marked with <span className="text-red-400">*</span> are required. Enter all amounts in ₹ crore.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Company Name <span className="text-red-400">*</span></label>
                  <input value={companyName} onChange={e => setCompanyName(e.target.value)} className="nebula-input" placeholder="Acme Corporation Ltd" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Registered On</label>
                  <input type="date" value={registeredOn} onChange={e => setRegisteredOn(e.target.value)} className="nebula-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Industry / Sector <span className="text-red-400">*</span></label>
                  <input value={industry} onChange={e => setIndustry(e.target.value)} className="nebula-input" placeholder="e.g. Manufacturing, Technology" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Country <span className="text-red-400">*</span></label>
                  <input value={country} onChange={e => setCountry(e.target.value)} className="nebula-input" placeholder="e.g. India" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">State <span className="text-red-400">*</span></label>
                  <input value={state} onChange={e => setState(e.target.value)} className="nebula-input" placeholder="e.g. Maharashtra" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">District <span className="text-red-400">*</span></label>
                  <input value={district} onChange={e => setDistrict(e.target.value)} className="nebula-input" placeholder="e.g. Mumbai" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Number of Employees</label>
                  <input type="number" value={employees} onChange={e => setEmployees(e.target.value)} className="nebula-input" placeholder="e.g. 250" />
                </div>
              </div>

              <div className="mt-6">
                <h3 className="text-sm font-semibold text-indigo-400 uppercase tracking-wider mb-3">Annual Imports <span className="text-red-400">*</span></h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Total Imports (₹ Cr) <span className="text-red-400">*</span></label>
                    <input
                      type="number"
                      step="0.01"
                      value={totalImportsINR}
                      onChange={e => { setTotalImportsINR(e.target.value); setTotalImportsUSD(crToUsdMn(e.target.value)); }}
                      className="nebula-input"
                      placeholder="e.g. 125.50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">USD Equivalent (USD Mn)</label>
                    <input
                      value={totalImportsUSD}
                      readOnly
                      tabIndex={-1}
                      className="nebula-input opacity-60 cursor-not-allowed"
                      placeholder="Auto-calculated"
                    />
                    <p className="text-xs text-gray-500 mt-1">Converted at ₹{USD_RATE}/USD</p>
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <h3 className="text-sm font-semibold text-indigo-400 uppercase tracking-wider mb-3">Annual Exports <span className="text-red-400">*</span></h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Total Exports (₹ Cr) <span className="text-red-400">*</span></label>
                    <input
                      type="number"
                      step="0.01"
                      value={totalExportsINR}
                      onChange={e => { setTotalExportsINR(e.target.value); setTotalExportsUSD(crToUsdMn(e.target.value)); }}
                      className="nebula-input"
                      placeholder="e.g. 78.20"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">USD Equivalent (USD Mn)</label>
                    <input
                      value={totalExportsUSD}
                      readOnly
                      tabIndex={-1}
                      className="nebula-input opacity-60 cursor-not-allowed"
                      placeholder="Auto-calculated"
                    />
                    <p className="text-xs text-gray-500 mt-1">Converted at ₹{USD_RATE}/USD</p>
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-300 mb-2">Brief on the Company <span className="text-red-400">*</span></label>
                <textarea value={companyBrief} onChange={e => setCompanyBrief(e.target.value)} className="nebula-input h-28 resize-none" placeholder="A short overview of your business..." />
              </div>
            </div>
          )}

          {/* ── STEP 2 ── */}
          {currentStep === 2 && (
            <div>
              <h2 className="text-xl font-bold text-white mb-2">Financial Performance</h2>
              <p className="text-gray-400 text-sm mb-6">Your financial history allows us to identify trends and recommend strategies. Enter all amounts in ₹ crore.</p>

              <h3 className="text-sm font-semibold text-indigo-400 uppercase tracking-wider mb-3">Last 3 Years — Sales, EBIT & P/L</h3>
              <div className="overflow-x-auto mb-8">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#2a2d3a]">
                      <th className="text-left text-gray-400 py-3 pr-4">Financial Year</th>
                      <th className="text-left text-gray-400 py-3 pr-4">Sales (₹ Cr)</th>
                      <th className="text-left text-gray-400 py-3 pr-4">EBIT (₹ Cr)</th>
                      <th className="text-left text-gray-400 py-3">Profit / Loss (₹ Cr)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {financials.map((row, i) => (
                      <tr key={i} className="border-b border-[#2a2d3a]/50">
                        <td className="py-3 pr-4"><input value={row.year}  onChange={e => updateFinancial(i, "year",  e.target.value)} className="nebula-input" placeholder="e.g. FY 2024" /></td>
                        <td className="py-3 pr-4"><input value={row.sales} onChange={e => updateFinancial(i, "sales", e.target.value)} className="nebula-input" placeholder="e.g. 450.00" /></td>
                        <td className="py-3 pr-4"><input value={row.ebit}  onChange={e => updateFinancial(i, "ebit",  e.target.value)} className="nebula-input" placeholder="e.g. 62.50" /></td>
                        <td className="py-3">     <input value={row.pnl}   onChange={e => updateFinancial(i, "pnl",   e.target.value)} className="nebula-input" placeholder="e.g. 41.80" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <h3 className="text-sm font-semibold text-indigo-400 uppercase tracking-wider mb-3">Key Suppliers</h3>
              {keySuppliers.map((s, i) => (
                <div key={i} className="flex gap-3 mb-3 items-center">
                  <input value={s.name}    onChange={e => updateRow(setKeySuppliers, keySuppliers, i, "name",    e.target.value)} className="nebula-input" placeholder="Supplier name" />
                  <input value={s.country} onChange={e => updateRow(setKeySuppliers, keySuppliers, i, "country", e.target.value)} className="nebula-input" placeholder="Country" />
                  {keySuppliers.length > 1 && <button onClick={() => removeRow(setKeySuppliers, keySuppliers, i)} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>}
                </div>
              ))}
              <button onClick={() => addRow(setKeySuppliers, keySuppliers, { name: "", country: "" })} className="flex items-center gap-2 text-indigo-400 text-sm mb-6 hover:text-indigo-300">
                <Plus className="w-4 h-4" /> Add Supplier
              </button>

              <h3 className="text-sm font-semibold text-indigo-400 uppercase tracking-wider mb-3">Key Buyers</h3>
              {keyBuyers.map((b, i) => (
                <div key={i} className="flex gap-3 mb-3 items-center">
                  <input value={b.name}    onChange={e => updateRow(setKeyBuyers, keyBuyers, i, "name",    e.target.value)} className="nebula-input" placeholder="Buyer name" />
                  <input value={b.country} onChange={e => updateRow(setKeyBuyers, keyBuyers, i, "country", e.target.value)} className="nebula-input" placeholder="Country" />
                  {keyBuyers.length > 1 && <button onClick={() => removeRow(setKeyBuyers, keyBuyers, i)} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>}
                </div>
              ))}
              <button onClick={() => addRow(setKeyBuyers, keyBuyers, { name: "", country: "" })} className="flex items-center gap-2 text-indigo-400 text-sm hover:text-indigo-300">
                <Plus className="w-4 h-4" /> Add Buyer
              </button>
            </div>
          )}

          {/* ── STEP 3 ── */}
          {currentStep === 3 && (
            <div>
              <h2 className="text-xl font-bold text-white mb-2">Debt & Investment Profile</h2>
              <p className="text-gray-400 text-sm mb-6">All fields are required. INR amounts in crore, foreign currency amounts in millions. Dates must be in DD-MM-YYYY format.</p>

              <h3 className="text-sm font-semibold text-indigo-400 uppercase tracking-wider mb-3">Debt Profile <span className="text-red-400">*</span></h3>
              {debtProfile.map((d, i) => (
                <div key={i} className="nebula-card p-4 mb-3 border border-[#2a2d3a]">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Debt Type <span className="text-red-400">*</span></label>
                      <select value={d.debt_type} onChange={e => updateRow(setDebtProfile, debtProfile, i, "debt_type", e.target.value)} className="nebula-input text-sm">
                        <option value="">Select...</option>
                        {DEBT_TYPES.map(t => <option key={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Provider / Bank <span className="text-red-400">*</span></label>
                      <input value={d.provider} onChange={e => updateRow(setDebtProfile, debtProfile, i, "provider", e.target.value)} className="nebula-input text-sm" placeholder="e.g. HDFC Bank" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Currency <span className="text-red-400">*</span></label>
                      <select value={d.currency} onChange={e => updateRow(setDebtProfile, debtProfile, i, "currency", e.target.value)} className="nebula-input text-sm">
                        {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Loan Amount ({debtUnitLabel(d.currency)}) <span className="text-red-400">*</span></label>
                      <input type="number" step="0.01" value={d.loan_amount} onChange={e => updateRow(setDebtProfile, debtProfile, i, "loan_amount", e.target.value)} className="nebula-input text-sm" placeholder="e.g. 25.00" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Outstanding Balance ({debtUnitLabel(d.currency)}) <span className="text-red-400">*</span></label>
                      <input type="number" step="0.01" value={d.outstanding_balance} onChange={e => updateRow(setDebtProfile, debtProfile, i, "outstanding_balance", e.target.value)} className="nebula-input text-sm" placeholder="e.g. 18.50" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Interest Rate % <span className="text-red-400">*</span></label>
                      <input type="number" step="0.01" value={d.interest_rate} onChange={e => updateRow(setDebtProfile, debtProfile, i, "interest_rate", e.target.value)} className="nebula-input text-sm" placeholder="e.g. 8.5" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Market Benchmark % <span className="text-red-400">*</span></label>
                      <input type="number" step="0.01" value={d.market_benchmark} onChange={e => updateRow(setDebtProfile, debtProfile, i, "market_benchmark", e.target.value)} className="nebula-input text-sm" placeholder="e.g. 9.5" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Start Date <span className="text-red-400">*</span></label>
                      <input value={d.start_date} onChange={e => updateRow(setDebtProfile, debtProfile, i, "start_date", formatDateInput(e.target.value))} className="nebula-input text-sm" placeholder="DD-MM-YYYY" maxLength={10} />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Maturity Date <span className="text-red-400">*</span></label>
                      <input value={d.maturity_date} onChange={e => updateRow(setDebtProfile, debtProfile, i, "maturity_date", formatDateInput(e.target.value))} className="nebula-input text-sm" placeholder="DD-MM-YYYY" maxLength={10} />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Payment Frequency <span className="text-red-400">*</span></label>
                      <select value={d.payment_frequency} onChange={e => updateRow(setDebtProfile, debtProfile, i, "payment_frequency", e.target.value)} className="nebula-input text-sm">
                        <option value="">Select...</option>
                        {PAYMENT_FREQUENCIES.map(f => <option key={f}>{f}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Rating <span className="text-red-400">*</span></label>
                      <input value={d.rating} onChange={e => updateRow(setDebtProfile, debtProfile, i, "rating", e.target.value)} className="nebula-input text-sm" placeholder="e.g. AAA" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Rating Provider <span className="text-red-400">*</span></label>
                      <input value={d.rating_provider} onChange={e => updateRow(setDebtProfile, debtProfile, i, "rating_provider", e.target.value)} className="nebula-input text-sm" placeholder="e.g. CRISIL" />
                    </div>
                  </div>
                  {debtProfile.length > 1 && (
                    <button onClick={() => removeRow(setDebtProfile, debtProfile, i)} className="text-red-400 hover:text-red-300 flex items-center gap-1 text-sm mt-3">
                      <Trash2 className="w-4 h-4" /> Remove
                    </button>
                  )}
                </div>
              ))}
              <button onClick={() => addRow(setDebtProfile, debtProfile, {
                debt_type: "", provider: "", currency: "INR", loan_amount: "", outstanding_balance: "",
                interest_rate: "", market_benchmark: "", start_date: "", maturity_date: "",
                payment_frequency: "", rating: "", rating_provider: "",
              })} className="flex items-center gap-2 text-indigo-400 text-sm mb-8 hover:text-indigo-300">
                <Plus className="w-4 h-4" /> Add Loan
              </button>

              <h3 className="text-sm font-semibold text-indigo-400 uppercase tracking-wider mb-3">Investment Profile <span className="text-red-400">*</span></h3>
              {investmentProfile.map((inv, i) => (
                <div key={i} className="nebula-card p-4 mb-3 border border-[#2a2d3a]">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Group <span className="text-red-400">*</span></label>
                      <select value={inv.group_type} onChange={e => updateRow(setInvestmentProfile, investmentProfile, i, "group_type", e.target.value)} className="nebula-input text-sm">
                        <option value="">Select group...</option>
                        {INVESTMENT_GROUPS.map(g => <option key={g}>{g}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Instrument Type <span className="text-red-400">*</span></label>
                      <select value={inv.instrument_type} onChange={e => updateRow(setInvestmentProfile, investmentProfile, i, "instrument_type", e.target.value)} className="nebula-input text-sm" disabled={!inv.group_type}>
                        <option value="">Select instrument...</option>
                        {(INSTRUMENT_MAP[inv.group_type] || []).map(t => <option key={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Instrument Name <span className="text-red-400">*</span></label>
                      <input value={inv.instrument_name} onChange={e => updateRow(setInvestmentProfile, investmentProfile, i, "instrument_name", e.target.value)} className="nebula-input text-sm" placeholder="e.g. HDFC FD" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Placed With <span className="text-red-400">*</span></label>
                      <input value={inv.placed_with} onChange={e => updateRow(setInvestmentProfile, investmentProfile, i, "placed_with", e.target.value)} className="nebula-input text-sm" placeholder="e.g. HDFC Bank" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Amount (₹ Cr) <span className="text-red-400">*</span></label>
                      <input type="number" step="0.01" value={inv.amount} onChange={e => updateRow(setInvestmentProfile, investmentProfile, i, "amount", e.target.value)} className="nebula-input text-sm" placeholder="e.g. 5.00" />
                    </div>
                    <div>
                      {inv.instrument_type === "Mutual Fund" ? (
                        <>
                          <label className="block text-xs text-gray-400 mb-1">NAV <span className="text-red-400">*</span></label>
                          <input type="number" step="0.01" value={inv.rate} onChange={e => updateRow(setInvestmentProfile, investmentProfile, i, "rate", e.target.value)} className="nebula-input text-sm" placeholder="e.g. 45.50" />
                        </>
                      ) : inv.instrument_type === "Stock" ? (
                        <>
                          <label className="block text-xs text-gray-400 mb-1">Stock Price <span className="text-red-400">*</span></label>
                          <input type="number" step="0.01" value={inv.rate} onChange={e => updateRow(setInvestmentProfile, investmentProfile, i, "rate", e.target.value)} className="nebula-input text-sm" placeholder="e.g. 1250.75" />
                        </>
                      ) : (
                        <>
                          <label className="block text-xs text-gray-400 mb-1">Rate % p.a. <span className="text-red-400">*</span></label>
                          <input type="number" step="0.01" value={inv.rate} onChange={e => updateRow(setInvestmentProfile, investmentProfile, i, "rate", e.target.value)} className="nebula-input text-sm" placeholder="e.g. 7.5" />
                        </>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Start Date <span className="text-red-400">*</span></label>
                      <input value={inv.start_date} onChange={e => updateRow(setInvestmentProfile, investmentProfile, i, "start_date", formatDateInput(e.target.value))} className="nebula-input text-sm" placeholder="DD-MM-YYYY" maxLength={10} />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">
                        End Date {inv.instrument_type !== "Mutual Fund" && inv.instrument_type !== "Stock" && <span className="text-red-400">*</span>}
                        {(inv.instrument_type === "Mutual Fund" || inv.instrument_type === "Stock") && <span className="text-gray-500">(Optional)</span>}
                      </label>
                      <input value={inv.end_date} onChange={e => updateRow(setInvestmentProfile, investmentProfile, i, "end_date", formatDateInput(e.target.value))} className="nebula-input text-sm" placeholder="DD-MM-YYYY" maxLength={10} />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Status <span className="text-red-400">*</span></label>
                      <select value={inv.status} onChange={e => updateRow(setInvestmentProfile, investmentProfile, i, "status", e.target.value)} className="nebula-input text-sm">
                        <option>Active</option>
                        <option>Matured</option>
                        <option>Redeemed</option>
                      </select>
                    </div>
                  </div>
                  {investmentProfile.length > 1 && (
                    <button onClick={() => removeRow(setInvestmentProfile, investmentProfile, i)} className="text-red-400 hover:text-red-300 flex items-center gap-1 text-sm mt-3">
                      <Trash2 className="w-4 h-4" /> Remove
                    </button>
                  )}
                </div>
              ))}
              <button onClick={() => addRow(setInvestmentProfile, investmentProfile, {
                group_type: "", instrument_type: "", instrument_name: "",
                placed_with: "", amount: "", start_date: "", end_date: "", rate: "", status: "Active",
              })} className="flex items-center gap-2 text-indigo-400 text-sm hover:text-indigo-300">
                <Plus className="w-4 h-4" /> Add Investment
              </button>
            </div>
          )}

          {/* ── STEP 4 ── */}
          {currentStep === 4 && (
            <div>
              <h2 className="text-xl font-bold text-white mb-2">People & Stakeholders</h2>
              <p className="text-gray-400 text-sm mb-6">Knowing your key decision-makers ensures we direct insights to the right people.</p>

              <h3 className="text-sm font-semibold text-indigo-400 uppercase tracking-wider mb-3">Promoter Details</h3>
              {promoterDetails.map((p, i) => (
                <div key={i} className="flex gap-3 mb-3 items-center">
                  <input value={p.name}  onChange={e => updateRow(setPromoterDetails, promoterDetails, i, "name",  e.target.value)} className="nebula-input" placeholder="Promoter name" />
                  <input value={p.stake} onChange={e => updateRow(setPromoterDetails, promoterDetails, i, "stake", e.target.value)} className="nebula-input" placeholder="Stake %" type="number" />
                  {promoterDetails.length > 1 && <button onClick={() => removeRow(setPromoterDetails, promoterDetails, i)} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>}
                </div>
              ))}
              <button onClick={() => addRow(setPromoterDetails, promoterDetails, { name: "", stake: "" })} className="flex items-center gap-2 text-indigo-400 text-sm mb-8 hover:text-indigo-300">
                <Plus className="w-4 h-4" /> Add Promoter
              </button>

              <h3 className="text-sm font-semibold text-indigo-400 uppercase tracking-wider mb-3">Key Personnel</h3>
              {keyPersonnel.map((p, i) => (
                <div key={i} className="flex gap-3 mb-3 items-center">
                  <input value={p.role} onChange={e => updateRow(setKeyPersonnel, keyPersonnel, i, "role", e.target.value)} className="nebula-input" placeholder="Role e.g. CEO" />
                  <input value={p.name} onChange={e => updateRow(setKeyPersonnel, keyPersonnel, i, "name", e.target.value)} className="nebula-input" placeholder="Full name" />
                  {keyPersonnel.length > 1 && <button onClick={() => removeRow(setKeyPersonnel, keyPersonnel, i)} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>}
                </div>
              ))}
              <button onClick={() => addRow(setKeyPersonnel, keyPersonnel, { role: "", name: "" })} className="flex items-center gap-2 text-indigo-400 text-sm mb-8 hover:text-indigo-300">
                <Plus className="w-4 h-4" /> Add Person
              </button>

              <h3 className="text-sm font-semibold text-indigo-400 uppercase tracking-wider mb-3">Statutory Auditor</h3>
              <textarea value={auditorDetails} onChange={e => setAuditorDetails(e.target.value)} className="nebula-input h-24 resize-none" placeholder="Name of audit firm, partner name, contact details..." />
            </div>
          )}

          {/* ── STEP 5 ── */}
          {currentStep === 5 && (
            <div>
              <h2 className="text-xl font-bold text-white mb-2">Audited Balance Sheets</h2>
              <p className="text-gray-400 text-sm mb-6">Upload your audited financial statements. Optional but helps build more accurate insights.</p>
              <div className="space-y-4">
                {[
                  { label: "Balance Sheet — Year 1", state: doc1, setter: setDoc1 },
                  { label: "Balance Sheet — Year 2", state: doc2, setter: setDoc2 },
                  { label: "Balance Sheet — Year 3", state: doc3, setter: setDoc3 },
                ].map((doc, i) => (
                  <div key={i} className="nebula-card p-5">
                    <label className="block text-sm font-medium text-gray-300 mb-3">{doc.label}</label>
                    <div className="flex items-center gap-4">
                      <label className="nebula-btn-primary cursor-pointer text-sm px-4 py-2">
                        {doc.state ? "Change File" : "Upload PDF"}
                        <input type="file" accept=".pdf" className="hidden" onChange={e => doc.setter(e.target.files[0])} />
                      </label>
                      {doc.state
                        ? <span className="text-emerald-400 text-sm">✓ {doc.state.name}</span>
                        : <span className="text-gray-500 text-sm">No file selected</span>}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 p-4 rounded-xl bg-indigo-500/10 border border-indigo-500/20">
                <p className="text-indigo-300 text-sm">📄 Accepted format: PDF only. Maximum 10MB per file.</p>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-[#2a2d3a]">
            <button onClick={() => setCurrentStep(s => s - 1)} disabled={currentStep === 1}
              className="flex items-center gap-2 px-5 py-2.5 border border-[#2a2d3a] rounded-xl text-gray-400 hover:text-white hover:border-indigo-500 transition-all disabled:opacity-30 disabled:cursor-not-allowed">
              <ChevronLeft className="w-4 h-4" /> Previous
            </button>
            <span className="text-gray-500 text-sm">Step {currentStep} of {steps.length}</span>
            {currentStep < steps.length ? (
              <button onClick={handleNext} className="flex items-center gap-2 nebula-btn-primary px-5 py-2.5">
                Next <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button onClick={handleSubmit} disabled={loading} className="flex items-center gap-2 nebula-btn-primary px-5 py-2.5">
                {loading
                  ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  : "Submit Profile"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
